import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import { 
  Button, 
  Col, 
  Form, 
  Row, 
  Table, 
  Spinner, 
  Badge,
  Card
} from 'react-bootstrap';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { ArrowLeft, Download, FileEarmarkSpreadsheet, Printer } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';

const StorageLocationStReport = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();

    const accessToken = localStorage.getItem("authToken");
    const [selectedStorageLocation, setSelectedStorageLocation] = useState('');
    const [selectedStorageLocationName, setSelectedStorageLocationName] = useState('');
    const [storageLocations, setStorageLocations] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState('');
    const [selectedRMCategory, setSelectedRMCategory] = useState('');
    const [selectedRawMaterials, setSelectedRawMaterials] = useState('');
    const [loading, setLoading] = useState(false);
    const [stockReportData, setStockReportData] = useState([]);
    const [rmMaincategoryList, setRmMaincategoryList] = useState([]);
    const [selectedRMSubCategory, setSelectedRMSubCategory] = useState('');
    const [rawMaterialList, setRawMaterialList] = useState([]);
    const [rmSubCategoryList, setRmSubCategoryList] = useState([]);
    const [clicked, setClicked] = useState(false);
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    // Main category options
    const mainCategoryOptions = rmMaincategoryList?.map((rmcat) => ({
        value: rmcat?._id,
        label: rmcat?.name,
    }));   

    // Subcategory options filtered by selected main category
    const subCategoryOptions = rmSubCategoryList
        ?.filter(subCat => selectedRMCategory ? subCat.mainCategory === selectedRMCategory : true)
        ?.map(subCat => ({
            value: subCat?._id,
            label: subCat?.name,
        }));

    // Raw material options filtered by selected main category and subcategory
    const rawMaterialOptions = rawMaterialList
        ?.filter(rm => {
            const matchesMainCategory = selectedRMCategory ? rm.mainCategory._id === selectedRMCategory : true;
            const matchesSubCategory = selectedRMSubCategory ? rm.subCategory?._id === selectedRMSubCategory : true;
            return matchesMainCategory && matchesSubCategory;
        })
        ?.map(rm => ({
            value: rm?._id,
            label: rm?.name,
        }));

    // Storage location options
    const storageLocationOptions = storageLocations?.map(location => ({
        value: location?._id,
        label: location?.name,
    }));

    const getStorageLocations = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("storage-location", { headers });

            if (response?.data?.status === true) {
                const activeLocations = response?.data?.data?.storageLocations;
                setStorageLocations(activeLocations);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const getRawMaterialsMainCategory = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("masters/rm-main-category/get", {
                headers,
            });

            if (response?.data?.status === true) {
                setRmMaincategoryList(response?.data?.data?.categories);
            }
        } catch (error) {
            handleApiError(error);
        }
    };
    
    const getRawMaterials = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            const response = await axios.get(`raw-material`, { headers });

            if (response?.data?.status === true) {
                setRawMaterialList(response?.data?.data?.rawMaterials);
                
                // Extract unique subcategories from raw materials
                const subCategories = response.data.data.rawMaterials
                    .map(rm => rm.subCategory)
                    .filter((subCat, index, self) => 
                        subCat && self.findIndex(t => t?._id === subCat?._id) === index
                    );
                
                setRmSubCategoryList(subCategories);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setClicked(true);
        setLoading(true);
        
        const adjustedStartDate = new Date(startDate?.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate?.setHours(23, 59, 59, 999));
        
        await getAllStockReport(
            adjustedStartDate, 
            adjustedEndDate, 
            selectedStorageLocation, 
            selectedRMCategory, 
            selectedRMSubCategory, 
            selectedRawMaterials
        );
        
        // toast.success("Report generated successfully", {
        //     position: "top-right",
        //     autoClose: 2000,
        //     theme: "colored",
        // });
        setLoading(false);
    }

    const handleEndDateChange = (date) => {
        if (date && startDate && date < startDate) {
            setError('End date cannot be earlier than start date');
        } else {
            setError('');
            setEndDate(date);
        }
    };

    const handleDownloadExcel = async () => {
        setDownloadBtnLoading(true);
        const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        getAllStockReport(adjustedStartDate, adjustedEndDate, selectedStorageLocation, selectedRMCategory, selectedRMSubCategory, selectedRawMaterials, true);
    };

    const getAllStockReport = async (start, end, storageLocation, rmCategory, subCategory, rawMaterial, download) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                "content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }

            let url = `raw-material/get-storage-location-stock-transfer-report?startDate=${start.toISOString().split("T")[0]}&endDate=${end.toISOString().split("T")[0]}`

            if (storageLocation) {
                url += `?storageLocationId=${storageLocation}`;
            }

            if (rmCategory) {
                url += `&mainCategory=${rmCategory}`;
            }

            if (subCategory) {
                url += `&subCategory=${subCategory}`;
            }

            if (rawMaterial) {
                url += `&rawMaterial=${rawMaterial}`;
            }

            if (download) {
                url += `&isDownload=${download}`
            }

            const response = await axios.get(url, { 
                headers,
                responseType: download ? 'blob' : 'json'
            });

            if (download) {
                // Create a blob from the response data
                const blob = new Blob([response.data], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                });
                
                // Create a link element
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                
                // Set the filename for the download
                const fileName = `${selectedStorageLocationName} Stock Report (${start.toISOString().split("T")[0]} - ${end.toISOString().split("T")[0]}).xlsx`;
                link.download = fileName;
                
                // Append the link to the body and trigger the download
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
        
                toast.success('Excel file downloaded successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light"
                });
            } else {
                setStockReportData(response?.data?.data?.data || []);
                toast.success("Report generated successfully", {
                    position: "top-right",
                    autoClose: 2000,
                    theme: "light",
                });
            }            
        } catch (error) {
            setStockReportData([]);
            handleApiError(error);
        } finally {
            setDownloadBtnLoading(false);
        }
    };

    const handleApiError = (error) => {
        console.log("Error :", error);
        
        if (error?.response) {
            let errorMessage = "";
            if (error.response?.status === 406) {
                errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
            } else if (error.response?.status === 404) {
                errorMessage = error?.response?.data?.data?.message;
            } else if (error.response?.status === 412) {
                errorMessage = error?.response?.data?.data?.message;
            } else if (error.response?.status === 500) {
                errorMessage = error?.response?.data?.error;
            }

            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        }
    };

    const handleMainCategoryChange = (selectedOption) => {
        const value = selectedOption?.value || '';
        setSelectedRMCategory(value);
        setSelectedRMSubCategory(''); // Reset subcategory when main category changes
        setSelectedRawMaterials(''); // Reset raw material when main category changes
    };

    const handleSubCategoryChange = (selectedOption) => {
        const value = selectedOption?.value || '';
        setSelectedRMSubCategory(value);
        setSelectedRawMaterials(''); // Reset raw material when subcategory changes
    };

    const handleRawMaterialChange = (selectedOption) => {
        setSelectedRawMaterials(selectedOption?.value || '');
    };

    const handleStorageLocationChange = (selectedOption) => {
        setSelectedStorageLocation(selectedOption?.value || '');
        setSelectedStorageLocationName(selectedOption?.label);
    };

    useEffect(() => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
        const end = new Date(currentDate.setHours(23, 59, 59, 999));
        
        setStartDate(start);
        setEndDate(end);
        getStorageLocations();
        getRawMaterialsMainCategory();
        getRawMaterials();
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-0 fw-bold d-inline-block">
                                <i className="bi bi-pin-map me-2"></i>STORAGE LOCATION STOCK REPORT
                            </h4>
                        </div>
                        <div>
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={() => navigate('/reports/all-reports')}
                                className="me-3 d-flex align-items-center fw-semibold"
                                style={{ height: '38px' }}
                            >
                                <ArrowLeft className="me-1" /> Back to Reports
                            </Button>
                        </div>
                    </Card.Header>
                    <hr className='mt-0 mb-0' /> 
                        
                    <Card.Body>
                        {/* Filters Section */}
                        <div className="p-3 bg-light rounded-3">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group controlId="storageLocationSelect">
                                            <Form.Label className="fw-semibold">
                                                Storage Location
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                options={storageLocationOptions}
                                                value={storageLocationOptions.find(option => option.value === selectedStorageLocation) || null}
                                                onChange={handleStorageLocationChange}
                                                placeholder="Select Storage Location"
                                                className="border-secondary"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderColor: '#6c757d',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="startDate">
                                            <Form.Label className="fw-semibold">
                                                Start Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => {
                                                    setStartDate(date);
                                                    // Reset end date if it's outside the allowed 60-day range
                                                    if (endDate && date && endDate > new Date(date.getTime() + 60 * 24 * 60 * 60 * 1000)) {
                                                    setEndDate(null);
                                                    }
                                                }}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Start Date"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="endDate">
                                            <Form.Label className="fw-semibold">
                                            End Date
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => {
                                                    if (startDate && date > new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000)) {
                                                        setError("End date cannot be more than 60 days after start date");
                                                    } else {
                                                        setError("");
                                                        handleEndDateChange(date);
                                                    }
                                                }}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="End Date"
                                                className="form-control border-secondary"
                                                minDate={startDate}
                                                maxDate={startDate ? new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000) : null}
                                            />
                                            {error && <div className="text-danger small mt-1">{error}</div>}
                                        </Form.Group>
                                    </Col>


                                    <Col md={4}>
                                        <Form.Group controlId="rmCategory">
                                            <Form.Label className="fw-semibold">
                                                RM Main Category
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                value={mainCategoryOptions.find(option => option.value === selectedRMCategory)}
                                                onChange={handleMainCategoryChange}
                                                options={mainCategoryOptions}
                                                placeholder="Select Main RM Categories"
                                                className="border-secondary"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderColor: '#6c757d',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="g-3 mt-2">
                                    <Col md={4}>
                                        <Form.Group controlId="subCategorySelect">
                                            <Form.Label className="fw-semibold">
                                                RM Sub Category
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                isSearchable
                                                placeholder="RM Sub Category"
                                                options={subCategoryOptions}
                                                value={subCategoryOptions.find(option => option.value === selectedRMSubCategory) || null}
                                                onChange={handleSubCategoryChange}
                                                className="border-secondary"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderColor: '#6c757d',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col md={4}>
                                        <Form.Group controlId="rawMaterial">
                                            <Form.Label className="fw-semibold">
                                                Raw Material
                                            </Form.Label>
                                            <Select
                                                isClearable
                                                value={rawMaterialOptions.find(option => option.value === selectedRawMaterials)}
                                                onChange={handleRawMaterialChange}
                                                options={rawMaterialOptions}
                                                placeholder="Select Raw Material"
                                                className="border-secondary"
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        borderColor: '#6c757d',
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="w-100"
                                            // disabled={!startDate || !endDate || !selectedStorageLocation}
                                        >
                                            {loading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>

                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="outline-success"
                                            className="w-100 fw-semibold"
                                            disabled={!stockReportData?.length}
                                            onClick={handleDownloadExcel}
                                        >
                                            <FileEarmarkSpreadsheet className="me-2" />
                                            { downloadBtnLoading ? <Spinner size='sm' /> : 'Download Excel' }
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>

                        {/* Results Section */}
                        {!clicked ? (
                            <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3 mt-2">
                                <div className="text-center">
                                    <i className="bi bi-clipboard-data fs-1 text-muted"></i>
                                    <h5 className="text-muted mt-3">
                                        Please select filters to view stock report
                                    </h5>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive border rounded-3 mt-3">
                                <Table striped bordered hover className="mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th width="50">#</th>
                                            <th>Raw Material Details</th>
                                            <th>Storage Location</th>
                                            <th className="text-center">Credited Qty</th>
                                            <th className="text-center">Debited Qty</th>
                                            <th className="text-center">Available Qty</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    <Spinner animation="border" variant="primary" />
                                                    <p className="mt-2 mb-0">Loading transactions...</p>
                                                </td>
                                            </tr>
                                        ) : stockReportData?.length > 0 ? (
                                            stockReportData.map((transaction, index) => {
                                                // Determine badge color based on status
                                                let badgeVariant;
                                                switch(transaction?.status) {
                                                    case "INSTOCK":
                                                        badgeVariant = "success";
                                                        break;
                                                    case "OUTOFSTOCK":
                                                        badgeVariant = "danger";
                                                        break;
                                                    case "LOWSTOCK":
                                                        badgeVariant = "warning";
                                                        break;
                                                    default:
                                                        badgeVariant = "secondary";
                                                }

                                                return (
                                                    <tr key={index}>
                                                        <td className="text-muted">
                                                            {index + 1}
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold">{transaction?.rawMaterial}</div>
                                                            <small className="text-primary">
                                                                {transaction?.mainRmCategory} &gt; {transaction?.subRmCategory}
                                                            </small><br />
                                                        </td>
                                                        <td>
                                                            {transaction?.storageLocation || 'N/A'}
                                                        </td>
                                                        <td className="text-center text-success">
                                                            {transaction?.totalCredit?.toFixed(2) || '0.00'} {transaction?.unit}
                                                        </td>
                                                        <td className="text-center text-danger">
                                                            {transaction?.totalDebit?.toFixed(2) || '0.00'} {transaction?.unit}
                                                        </td>
                                                        <td className="text-center fw-semibold text-muted">
                                                            {transaction?.availableStock?.toFixed(2)}  {transaction?.unit}
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg={badgeVariant} className="px-1 py-1 w-75 fw-semibold">
                                                                {transaction?.status === "INSTOCK" && "In Stock"}
                                                                {transaction?.status === "OUTOFSTOCK" && "Out of Stock"}
                                                                {transaction?.status === "LOWSTOCK" && "Low Stock"}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                                                    <p className="mt-2 mb-0 text-muted">
                                                        No transactions found for the selected criteria
                                                    </p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </React.Fragment>
    )
}

export default StorageLocationStReport;